/**
 * Validates whether the current hostname is an authorized domain for FansFirst.
 * Prevents unauthorized hosting/cloning on fake, phishing, or typo-squatted domains.
 */

export const ALLOWED_HOST_PATTERNS = [
  // Local development
  /^localhost$/i,
  /^127\.0\.0\.1$/i,
  /^\[::1\]$/i,
  /^localhost:\d+$/i,

  // FansFirst production & staging domains (*.fansfirst.ca, fansfirst.ca)
  /^(?:[a-zA-Z0-9-]+\.)*fansfirst\.ca$/i,

  // AWS Amplify preview/staging domains (*.amplifyapp.com)
  /^(?:[a-zA-Z0-9-]+\.)*amplifyapp\.com$/i,
];

/**
 * Checks if a hostname matches any authorized patterns.
 * @param {string} hostname
 * @returns {boolean}
 */
export function isAllowedDomain(hostname) {
  if (!hostname || typeof hostname !== "string") {
    return false;
  }
  const cleanHost = hostname.trim().toLowerCase();
  // Strip port if present, while properly handling IPv6 bracketed hosts like [::1]:3000
  let hostWithoutPort = cleanHost;
  if (cleanHost.startsWith("[") && cleanHost.includes("]")) {
    hostWithoutPort = cleanHost.substring(0, cleanHost.indexOf("]") + 1);
  } else {
    hostWithoutPort = cleanHost.split(":")[0];
  }
  return ALLOWED_HOST_PATTERNS.some((pattern) => pattern.test(hostWithoutPort) || pattern.test(cleanHost));
}

/**
 * Enforces domain security in the browser runtime.
 * If running under an unauthorized domain, it stops app execution,
 * wipes the DOM to render a blank screen, and prevents React from mounting.
 *
 * @param {Object} [options]
 * @param {Function} [options.onViolation]
 * @returns {boolean} true if authorized, false if blocked
 */
export function enforceDomainSecurity({
  onViolation,
} = {}) {
  if (typeof window === "undefined" || !window.location) {
    return true;
  }

  const hostname = window.location.hostname;
  if (!isAllowedDomain(hostname)) {
    if (typeof onViolation === "function") {
      try {
        onViolation(hostname);
      } catch (err) {
        // Suppress callback errors during lockdown
      }
    }

    try {
      if (document && document.documentElement) {
        document.documentElement.innerHTML = "";
      }
    } catch (e) {
      // Ignore DOM wipe errors if any
    }

    return false;
  }

  return true;
}
